import React, { useState, useEffect } from 'react';
import api from '../services/api';

/**
 * Visualizacao do documento dentro do sistema.
 *
 * Busca o preview como blob em vez de apontar o iframe direto para a rota:
 * assim da para distinguir "convertendo" de "falhou" e mostrar o motivo, o
 * que um iframe apontado para a URL nao permite.
 */
const ERROR_BY_STATUS = {
  403: 'Você não tem acesso a este documento.',
  404: 'Versão do documento não encontrada.',
  415: 'Este formato não tem visualização no sistema.',
  502: 'Não foi possível converter o documento para visualização.',
};

export default function DocumentViewer({ documentId, versionId, canDownload, onDownload }) {
  const [src, setSrc] = useState(null);
  const [state, setState] = useState('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setState('loading');
      setSrc(null);

      const path = versionId
        ? `/documents/${documentId}/preview/${versionId}`
        : `/documents/${documentId}/preview`;

      try {
        // HEAD apenas para saber se da certo: o servidor roda a conversao (ou
        // pega do cache) e responde sem corpo. O iframe entao busca a URL de
        // verdade — e nao um blob — para que o Content-Disposition defina o
        // nome do arquivo quando o usuario salvar pelo leitor de PDF.
        await api.head(path);
        if (cancelled) return;
        setSrc(`/api${path}`);
        setState('ready');
      } catch (err) {
        if (cancelled) return;
        setMessage(ERROR_BY_STATUS[err.response?.status] || 'Não foi possível exibir este documento.');
        setState('error');
      }
    }

    load();
    return () => { cancelled = true; };
  }, [documentId, versionId]);

  if (state === 'loading') {
    return (
      <div className="bg-surface-100 border rounded-md flex flex-col items-center justify-center h-[720px] text-ink-muted">
        <div className="text-sm">Preparando visualização...</div>
        <div className="text-xs mt-1">Documentos do Office são convertidos para PDF na primeira abertura.</div>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="bg-surface-100 border rounded-md p-6 text-center">
        <div className="text-sm text-ink-secondary">{message}</div>
        {canDownload && (
          <button className="btn btn-sm btn-secondary mt-3" onClick={onDownload}>
            Baixar arquivo original
          </button>
        )}
      </div>
    );
  }

  // A barra do leitor fica sempre oculta. O PDF servido na visualizacao e
  // limpo, sem carimbo — se a barra aparecesse, o botao de salvar dela
  // entregaria uma copia sem marca d'agua, contornando /download e /print.
  // Nao e barreira absoluta, mas e o que fecha o caminho obvio.
  const frameSrc = `${src}#toolbar=0&navpanes=0`;

  return (
    <div className="bg-surface-100 border rounded-md overflow-hidden">
      <iframe
        src={frameSrc}
        title="Visualização do documento"
        className="w-full h-[720px] border-0"
      />
    </div>
  );
}
